import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { InvoiceListRow } from "@themislex/shared";
import { useApi } from "../../api/hooks";
import { InvoiceTable } from "../../components/InvoiceTable";
import { EmptyState, ErrorNote, PageHeader, Select, Spinner } from "../../components/ui";

export default function Invoices() {
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState("");

  const status = params.get("status") ?? "";

  const search = new URLSearchParams();
  if (query.trim()) search.set("q", query.trim());
  if (status) search.set("status", status);

  const { data, error, loading } = useApi<{ invoices: InvoiceListRow[] }>(
    `/api/invoices?${search.toString()}`,
  );

  const update = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  return (
    <>
      <PageHeader
        title="Invoices"
        subtitle="Every invoice raised, with live payment status."
        actions={
          <Link to="/desk/invoices/new" className="btn btn-primary no-underline">
            New invoice
          </Link>
        }
      />

      <div className="card mb-6 grid gap-4 p-4 sm:grid-cols-2">
        <div>
          <label className="label" htmlFor="invoice-search">
            Search
          </label>
          <input
            id="invoice-search"
            className="input"
            type="search"
            placeholder="Invoice number, client or matter…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <Select
          label="Status"
          value={status}
          onChange={(e) => update("status", e.target.value)}
        >
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="pending">Payment due</option>
          <option value="overdue">Overdue</option>
          <option value="paid">Paid</option>
        </Select>
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}
      {loading && (
        <div className="flex justify-center py-16 text-muted">
          <Spinner className="h-6 w-6" />
        </div>
      )}

      {data &&
        (data.invoices.length === 0 ? (
          <EmptyState
            title="No invoices found."
            hint="Adjust the filters, or raise a new invoice."
            action={
              <Link to="/desk/invoices/new" className="btn btn-primary no-underline">
                New invoice
              </Link>
            }
          />
        ) : (
          <div className="card p-2">
            <InvoiceTable invoices={data.invoices} showClient lawyerView />
          </div>
        ))}
    </>
  );
}
